import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { once } from 'lodash-es';
import TitleAndDescription from 'app/scripts/components/common/title-and-description';
import { AnalyticsProvider } from 'app/scripts/services/analytics-provider';
import tmsBE from 'app/libraries/fed-resources/resources/tms-be';
import AppAdsTextGenerator from 'app/scripts/components/common/inputs/customs/app-ads-text-generator';
import { getProcessedAdsText, getSellerIdToUse } from 'app/scripts/utils/ads-texts-helpers';
import { useTenantConfig } from '../../../../tenant-config/useTenantConfig';
import { ADS_TEXT } from '../../../../tenant-config/tenantConfigConstants';

const LEARN_MORE_URL = 'https://iabtechlab.com/ads-txt/';

const AdsTextsGenerator = ({ spotId }) => {
  const textareaEl = useRef(null);
  const [isFetching, setIsFetching] = useState(false);
  const [adsText, setAdsText] = useState('');
  const [isTextareaVisible, setIsTextareaVisible] = useState(false);

  const [configuration, loading] = useTenantConfig(ADS_TEXT);

  const [isSellerIdFetched, setIsSellerIdFetched] = useState(false);
  const [aylSellerId, setAylSellerId] = useState('');
  const [sellerId, setSellerId] = useState('');

  useEffect(() => {
    const fetchSellerId = async () => {
      const {
        body: { ayl_seller_id, seller_id }
      } = await tmsBE.getSellerId(spotId);

      setSellerId(seller_id);
      setAylSellerId(ayl_seller_id);
      setIsSellerIdFetched(true);
    };

    fetchSellerId();
  }, [spotId]);

  useEffect(() => {
    if (loading || !isSellerIdFetched || !configuration) {
      return;
    }

    async function fetchAdsText() {
      try {
        const { url, sellerIdOverrides = [] } = configuration;
        const adsTextTemplateResponse = await fetch(url);
        const adsTextTemplate = await adsTextTemplateResponse.text();

        const sellerIdToUse = getSellerIdToUse(sellerId, sellerIdOverrides, spotId);
        const processedAdsText = getProcessedAdsText(adsTextTemplate, sellerIdToUse, aylSellerId);

        setAdsText(processedAdsText);
        setIsFetching(false);
      } catch (err) {
        AnalyticsProvider.track('ads.txt fetch error', err.message);
      }
    }

    setIsFetching(true);
    fetchAdsText();
  }, [aylSellerId, configuration, isSellerIdFetched, loading, sellerId, spotId]);

  function trackAdsTextOnce() {
    return once(() => AnalyticsProvider.track('ads.txt clicked', event));
  }

  return (
    <div className="ads-text-generator">
      <TitleAndDescription
        title={
          <div>
            <div className="ads-text-title">
              Ads.txt
              <div className="ads-text-badge">IMPORTANT</div>
            </div>
            <a
              className="ads-text-link"
              target="_blank"
              rel="noopener noreferrer"
              href={LEARN_MORE_URL}
            >
              Learn more
            </a>
          </div>
        }
        description={
          <>
            <div>
              Adding OpenWeb’s list of authorized digital sellers to your site’s Ads.txt file is
              mandatory to allow OpenWeb to serve ads on your domain.
            </div>
            <div className="ads-text-last-update">
              {!loading && configuration && <b>Last updated {configuration.updateDate}</b>}
            </div>
          </>
        }
      />

      <button
        className="ads-text-copy-button"
        disabled={loading || isFetching || !adsText}
        onClick={() => {
          setIsTextareaVisible(true);
          textareaEl.current.value = adsText;
          trackAdsTextOnce();
        }}
      >
        Get Ads.txt
      </button>
      <textarea className={isTextareaVisible ? 'isVisible' : ''} ref={textareaEl} />

      <AppAdsTextGenerator
        isSellerIdFetched={isSellerIdFetched}
        sellerId={sellerId}
        aylSellerId={aylSellerId}
        spotId={spotId}
      />
    </div>
  );
};

AdsTextsGenerator.displayName = 'AdsTextsGenerator';

AdsTextsGenerator.propTypes = {
  spotId: PropTypes.string.isRequired
};

export default React.memo(AdsTextsGenerator);
